<script setup>
import {getChooseAll, mainCategories} from "@/constants.js";
import Input from "@/Components/Input.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import Dropdown from "@/Components/Dropdown.vue";
import {computed, ref} from "vue";
import Card from "@/Components/Card.vue";
import {router, usePage} from "@inertiajs/vue3";
import Spinner from "@/Components/Icons/Spinner.vue";
import MaskIcon from "@/Components/MaskIcon.vue";
import {route} from "ziggy-js";
import {trans} from "laravel-vue-i18n";
import {clickOutSide as vClickOutside} from "@/vClickOutside.js";


const props = defineProps({
    searchValue: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false,
    },
    arrLatLetters: {
        type: Array
    },
    arrRuLetters: {
        type: Array
    },
    activeLetter: {
        type: String,
        default: ''
    },
    activeSort: {
        type: String,
        default: null
    },
})

const emit = defineEmits(['updateSearchValue', 'selectLetter', 'updateSort'])
const page = usePage();
const categories = ref([...page.props.categories])
if (categories.value.some(category => !!category.id)) {
    categories.value.unshift(getChooseAll())
}
const parentIds = categories.value.map(category => category.parent_id).filter(parentId => parentId !== null);

// Фильтруем массив, удаляя элементы, у которых id равен parent_id другого элемента
const filteredCategories = categories.value.filter(category => !parentIds.includes(category.id));


const statusDropDownCategory = ref(false),
    statusDropDownSort = ref(false),
    arrSorts = computed(() => {
        return [{id: 0, title: trans('Sort A-Z'), val: 'title_asc'}, {
            id: 1,
            title: trans('Sort Z-A'),
            val: 'title_desc'
        }]
    }),
    // Подпись селектора обязана описывать то, что на экране на самом деле. Раньше он всегда
    // открывался на "По алфавиту A-Z", хотя до первого клика сортировка не применялась вообще -
    // каталог выглядел неотсортированным при подписи, утверждающей обратное. Теперь без явного
    // выбора листинг реально идёт A-Z (Product::browsePaginate), а вот поисковая выдача - по
    // релевантности, и называть её алфавитной было бы тем же враньём в другую сторону.
    sortLabel = computed(() => {
        const explicit = arrSorts.value.find(sort => sort.val === props.activeSort)
        if (explicit) {
            return explicit.title
        }

        return props.searchValue ? trans('By relevance') : arrSorts.value[0].title
    }),
    // Двусторонняя связка вместо локальной копии: строку поиска держит Category/Show.vue, потому
    // что она обязана сбрасываться при выборе буквы. С ref(props.searchValue) значение
    // копировалось один раз при монтировании и дальше жило само по себе - родитель мог сколько
    // угодно чистить searchValue, в поле по-прежнему висел старый текст.
    search = computed({
        get: () => props.searchValue,
        set: (value) => emit('updateSearchValue', value)
    }),
    otherCategory = page.props.categories.filter(category => !mainCategories.includes(category.slug)),
    selectCategory = ref(page.props.category ? page.props.category.id : 0),
    categoryName = ref(categories.value.find(item => {
        return item.id === selectCategory.value
    }))



const inputEl = ref(null)

const focusInput = () => {
    inputEl.value.classList.add('grow')
};
const onFocusInput = () => {
    if (inputEl.value) {
        inputEl.value.classList.remove('grow')
    }

};

const clearInput = () => {
    search.value = '';
    onFocusInput();
}

const updateSelectCategory = (event) => {
    selectCategory.value = event
    categoryName.value = categories.value.find(item => item.id === event)

    if (!categoryName.value.id) {
        router.get(route('products.index'))
    } else {
        router.get(route('category', {slug: categoryName.value.slug}))
    }
}

const updateSort = (event) => {
    emit('updateSort', arrSorts.value.find(sort => sort.id === event).val)
}
</script>

<template>
    <div class="flex bg-white dark:bg-dark-blue shadow-md lg:pr-2.5 rounded-lg justify-between">
        <div class="flex justify-between lg:justify-start shrink w-full lg:w-auto">
            <div class="overflow-hidden rounded-l-lg flex-shrink-0">
                <template v-if="route().current('products.index')">
                    <img src="/assets/icons/category-all.svg" alt=""/>
                </template>
                <template v-if="route().current('category', 'games')">
                    <img src="/assets/icons/category-games.svg" alt=""/>
                </template>
                <template v-if="route().current('category', 'subscriptions')">
                    <img src="/assets/icons/category-subscriptions.svg" alt=""/>
                </template>
                <template
                    v-if="otherCategory.some(item => route().current('category', item.slug))">
                    <img src="/assets/icons/category-other.svg" alt=""/>
                </template>
            </div>
            <div class="px-3.5 py-4 w-full lg:w-auto">
                <div class="w-full flex justify-end lg:hidden">
                    <div class="flex flex-col items-end">
                        <Dropdown
                            align="right"
                            width="60" @close="statusDropDownCategory = false"
                            @open="statusDropDownCategory = true">
                            <template #trigger>
                <span class="flex rounded-md items-center">
                    <button type="button"
                            class="inline-flex dark:text-slate-200 gap-2 items-center text-xl leading-120 font-bold focus:outline-hidden transition ease-in-out whitespace-nowrap">
                        {{ categoryName.title }}
                        <MaskIcon src="/assets/icons/arrow.svg" ref="arrow" class="w-6 h-6 bg-[#21272A] dark:bg-white" :class="statusDropDownCategory ? 'rotate-180': 'rotate-0'"/>
                    </button>

                </span>
                            </template>
                            <template #content>
                                <div class="w-60 bg-dark-blue">
                                    <!-- Team Management -->
                                    <template v-for="category in filteredCategories">
                                        <dropdown-link as="button" @click="updateSelectCategory(category.id)"
                                                       class="p-1 border-slate-500 dark:border-slate-700 font-medium [&>*]:hover:bg-gray-600 [&>*]:dark:hover:text-slate-200 [&>*]:text-lg">
                                            {{ category.title }}
                                        </dropdown-link>
                                    </template>
                                </div>
                            </template>
                        </Dropdown>
                        <div class="text-sm text-left w-full lg:hidden text-slate-500 dark:text-slate-400 font-normal leading-120">
                            {{ search === ''
                                ? $t('Total :count', {count: page.props.product_count})
                                : $t('Found :count', {count: page.props.products.total}) }}
                        </div>
                    </div>
                </div>
                <Dropdown
                    class="hidden lg:block"
                    align="left"
                    width="60" @close="statusDropDownCategory = false"
                    @open="statusDropDownCategory = true">
                    <template #trigger>
                <span class="flex rounded-md items-center">
                    <button type="button"
                            class="inline-flex dark:text-slate-200 gap-2 items-center text-xl leading-120 font-bold focus:outline-hidden transition ease-in-out whitespace-nowrap">
                        {{ categoryName.title }}
                        <MaskIcon src="/assets/icons/arrow.svg" ref="arrow" class="w-6 h-6 bg-[#21272A] dark:bg-white" :class="statusDropDownCategory ? 'rotate-180': 'rotate-0'"/>
                    </button>

                </span>
                    </template>
                    <template #content>
                        <div class="w-60">
                            <!-- Team Management -->
                            <template v-for="category in filteredCategories">
                                <dropdown-link as="button" @click="updateSelectCategory(category.id)"
                                               class="border-b border-dashed border-slate-300 dark:border-slate-700 font-medium text-base">
                                    {{ category.title }}
                                </dropdown-link>
                            </template>
                        </div>
                    </template>
                </Dropdown>
                <div class="text-sm text-slate-500 hidden lg:block dark:text-slate-400 font-normal leading-120">
                    {{ search === ''
                        ? $t('Total :count', {count: page.props.product_count})
                        : $t('Found :count', {count: page.props.products.total}) }}
                </div>
            </div>
        </div>
        <div ref="inputEl" class="px-3.5 hidden lg:flex py-4 gap-4 items-center transition-all duration-1000">
            <Input :border-focus="true" v-model="search" :placeholder="$t('Search in category')" @onFocus="focusInput" v-click-outside="onFocusInput">
                <template #left-icon>
                    <MaskIcon src="/assets/icons/search.svg" class="w-4 h-[17px] bg-[#131933] dark:bg-slate-400"/>
                </template>
                <template #right-icon>
                    <div v-if="loading" class="flex">
                        <Spinner class="self-center"/>
                    </div>
                    <button v-else-if="search !== ''" @click="clearInput()">
                        <img src="/assets/icons/clear.svg" alt=""/>
                    </button>
                </template>
            </Input>
            <Dropdown align="left"
                      class="hidden lg:block"
                      width="auto"
                      @close="statusDropDownSort = false" @open="statusDropDownSort = true">
                <template #trigger>
                <span class="flex rounded-md items-center">
                    <button type="button"
                            class="whitespace-nowrap dark:text-slate-200 inline-flex gap-2 items-center text-sm font-medium leading-120 focus:outline-hidden transition ease-in-out">
                           {{ sortLabel }}
                        <MaskIcon src="/assets/icons/arrow.svg" ref="arrow" class="w-6 h-6 bg-[#21272A] dark:bg-white" :class="statusDropDownSort ? 'rotate-180': 'rotate-0'"/>
                    </button>

                </span>
                </template>
                <template #content>
                    <div class="">
                        <!-- Team Management -->

                        <template v-for="sort in arrSorts">
                            <dropdown-link as="button" class="border-b border-dashed border-slate-300 dark:border-slate-700"
                                           @click="updateSort(sort.id)">
                                {{ sort.title }}
                            </dropdown-link>
                        </template>
                    </div>
                </template>
            </Dropdown>
        </div>
    </div>

    <transition
        enter-active-class="ease-out duration-300"
        enter-from-class="opacity-0 h-0"
        enter-to-class="opacity-100 h-10"
        leave-active-class="ease-in duration-200 "
        leave-from-class="opacity-100 h-10"
        leave-to-class="opacity-0 h-0">
        <div v-show="search === '' && (arrLatLetters.length > 0 || arrRuLetters.length > 0)"
             class="hidden lg:flex px-2 py-2 bg-white dark:bg-dark-blue rounded-lg gap-1 items-center overflow-hidden">
            <div class="pr-3 text-slate-500 dark:text-slate-400 font-normal">{{ $t('Alphabetical search') }}:</div>
            <template v-for="symbol in arrLatLetters">
                <div
                    @click="$emit('selectLetter', symbol)"
                    :class="activeLetter === symbol ? 'bg-blue-600 dark:bg-blue-600 text-white dark:text-white' : 'bg-slate-600 dark:bg-gray-500 text-white dark:text-dark-blue'"
                    class="text-xs leading-full hover:bg-blue-600 dark:hover:bg-blue-600 transition cursor-pointer font-normal p-1 rounded-full w-3 h-4 flex items-center justify-center">
                    <span>{{ symbol }}</span></div>
            </template>
            <div class="w-3"></div>
            <template v-for="symbol in arrRuLetters">
                <div
                    @click="$emit('selectLetter', symbol)"
                    :class="activeLetter === symbol ? 'bg-blue-600 dark:bg-blue-600 text-white dark:text-white' : 'bg-slate-800 dark:bg-gray-400 text-white dark:text-dark-blue'"
                    class="text-xs leading-full cursor-pointer hover:bg-blue-600 dark:hover:bg-blue-600 transition font-normal p-1 rounded-full w-3 h-4 flex items-center justify-center">
                    <span>{{ symbol }}</span></div>
            </template>
            <!-- Единственный способ выйти из фильтра по букве, кроме повторного клика по ней:
                 до этого сброса не существовало вовсе, а сама буква ещё и переживала последующий
                 поиск, оставаясь в адресе. -->
            <button v-if="activeLetter"
                    @click="$emit('selectLetter', activeLetter)"
                    class="ml-2 text-xs leading-full text-slate-500 dark:text-slate-400 hover:text-blue-600 transition font-normal whitespace-nowrap">
                {{ $t('Reset') }}
            </button>
        </div>
    </transition>

    <Input :border-focus="true" class="lg:hidden" v-model="search" :placeholder="$t('Search in category')">
        <template #left-icon>
            <MaskIcon src="/assets/icons/search.svg" class="w-4 h-[17px] bg-[#131933] dark:bg-slate-400"/>
        </template>
        <template #right-icon>
            <div v-if="loading" class="flex">
                <Spinner class="self-center"/>
            </div>
            <button v-else-if="search !== ''" @click="search = ''">
                <img src="/assets/icons/clear.svg" alt=""/>
            </button>

        </template>
    </Input>

    <Card class="flex lg:hidden px-3.5 h-10 items-center justify-between w-full">
        <div class="text-sm text-slate-500 dark:text-slate-400 leading-full font-normal">{{ $t('Sort') }}:</div>
        <Dropdown align="left"
                  class=""
                  width="auto"
                  @close="statusDropDownSort = false" @open="statusDropDownSort = true">
            <template #trigger>
                <span class="flex rounded-md items-center">
                    <button type="button"
                            class="whitespace-nowrap dark:text-slate-200 inline-flex gap-2 items-center text-sm font-medium leading-120 focus:outline-hidden transition ease-in-out">
                           {{ sortLabel }}
                        <MaskIcon src="/assets/icons/arrow.svg" ref="arrow" class="w-6 h-6 bg-[#21272A] dark:bg-white" :class="statusDropDownSort ? 'rotate-180': 'rotate-0'"/>
                    </button>

                </span>
            </template>
            <template #content>
                <div class="bg-dark-blue">
                    <!-- Team Management -->
                    <template v-for="sort in arrSorts">
                        <dropdown-link as="button" class="border-slate-500 dark:border-slate-700 [&>*]:hover:bg-gray-600 [&>*]:dark:hover:text-slate-200 "
                                       @click="updateSort(sort.id)">
                            {{ sort.title }}
                        </dropdown-link>
                    </template>
                </div>
            </template>
        </Dropdown>
    </Card>
</template>

<style scoped>

</style>
